import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages } from 'react-intl';
import { CheckOutlined, LoadingOutlined, PlusOutlined, StopOutlined } from '@ant-design/icons';
import BaseModal from '@components/common/form/BaseModal';
import useFetch from '@hooks/useFetch';
import useFormField from '@hooks/useFormField';
import useTranslate from '@hooks/useTranslate';
import { commonMessage } from '@locales/intl';
import { checkImageValid, convertUrlToBase64, getFullImageUrl } from '@utils';
import { Button, Flex, Form, Image, List, Spin, Tabs } from 'antd';

import CropImageFieldModal from './CropImageFieldModal';

import styles from './index.module.scss';
const { TabPane } = Tabs;

const PAGE_SIZE = 102;

const messages = defineMessages({
    required: {
        id: 'form.field.required',
        defaultMessage: 'Please select an image',
    },
    title: 'Select Image',
    imageLibrary: 'Image Library',
    uploadImage: 'Upload Image',
    loadMore: 'Load more',
    loading: 'Loading...',
});

function ImagePickerModalField({
    name,
    label,
    value,
    onChange,
    uploadedImages,
    imagesApiConfig,
    imagesParams = {},
    onUploadFile,
    accept = 'image/jpeg, image/jpg, image/gif, image/bmp, image/png',
    required = false,
    formItemProps,
    valuePropName,
    fileList,
    imageUrl,
    isNoCrop = true,
    width = 120,
    height = 120,
    imagesRootUrl,
    ...props
}) {
    const url = getFullImageUrl(value,imagesRootUrl);
    const [isValid, setIsValid] = useState(false);
    
    useEffect(() => {
        checkImageValid(url).then(setIsValid);
    }, [value]);

    const translate = useTranslate();
    const { rules } = useFormField(props);

    const [selectedImage, setSelectedImage] = useState(value || null);
    const [visible, setVisible] = useState(false);

    const [allImages, setAllImages] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    const sentinelRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const isFetchingRef = useRef(false);

    const useInternalFetch = !!imagesApiConfig;

    const { execute: executeFetchImages } = useFetch(imagesApiConfig || {}, { immediate: false });

    const fetchImages = useCallback(
        (page) => {
            if (!imagesApiConfig || loadingMore || isFetchingRef.current) return;
            
            isFetchingRef.current = true;
            setLoadingMore(true);
            
            executeFetchImages({
                params: {
                    ...imagesParams,
                    page,
                    size: PAGE_SIZE,
                },
                onCompleted: (response) => {
                    const newItems = response?.data?.content || [];
                    const totalPages = response?.data?.totalPages || 1;

                    if (page === 0) {
                        setAllImages(newItems);
                    } else {
                        setAllImages((prev) => [...prev, ...newItems]);
                    }

                    setHasMore(page < totalPages - 1);
                    setCurrentPage(page);
                    setLoadingMore(false);
                    isFetchingRef.current = false;
                },
                onError: () => {
                    setLoadingMore(false);
                    isFetchingRef.current = false;
                },
            });
        },
        [imagesApiConfig, imagesParams, executeFetchImages, loadingMore],
    );

    useEffect(() => {
        if (!useInternalFetch || !visible) return;

        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting && hasMore && !loadingMore && !isFetchingRef.current) {
                    const nextPage = currentPage + 1;
                    fetchImages(nextPage);
                }
            },
            {
                root: scrollContainerRef.current,
                rootMargin: '100px',
                threshold: 0.1,
            },
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [useInternalFetch, visible, hasMore, loadingMore, currentPage, fetchImages]);

    const updatedRules = useMemo(() => {
        if (required) {
            return [
                ...(rules || []),
                {
                    required: !imageUrl && !fileList?.length,
                    message: translate.formatMessage(messages.required),
                },
            ];
        }
        return rules;
    }, [required, rules, imageUrl, fileList, translate]);

    const handleOk = async () => {
        if (selectedImage) {
            try {
                let finalImage = selectedImage;
                if (!selectedImage.startsWith('data:image')) {
                    finalImage = await convertUrlToBase64(selectedImage, imagesRootUrl);
                }
                onChange?.(finalImage);
            } catch {
                onChange?.(selectedImage);
            }
            setVisible(false);
            setSelectedImage(null);
        }
    };

    const handleCancel = () => {
        setVisible(false);
        setSelectedImage(null);
    };

    useEffect(() => {
        if (visible && useInternalFetch) {
            setAllImages([]);
            setCurrentPage(0);
            setHasMore(true);
            isFetchingRef.current = false;
            fetchImages(0);
        }
    }, [visible, useInternalFetch]);

    const displayImages = useInternalFetch ? allImages : (uploadedImages || []);

    return (
        <Form.Item
            {...formItemProps}
            label={label}
            name={name}
            rules={updatedRules}
            valuePropName={valuePropName || 'value'}
            style={{
                marginBottom: 16,
            }}
        >
            <div>
                <div style={{ position: 'relative', width: width, height: height }}>
                    {value && isValid && (
                        <Image
                            src={url}
                            alt=""
                            style={{
                                maxWidth: '100%',
                                border: '1px solid #ddd',
                                borderRadius: 8,
                                padding: 4,
                                width: width,
                                height: height,
                                background: '#171717',
                            }}
                            preview={false}
                        />
                    )}
                    <div onClick={() => setVisible(true)} style={{ width: width, height: height }} className={styles.upload}>
                        {!value && <PlusOutlined />}
                        <div
                            className="ant-upload-text"
                            style={
                                value
                                    ? { background: 'hsla(60,1%,76%,.541)', padding: '5px 7px', borderRadius: 15 }
                                    : {}
                            }
                        >
                            {value
                                ? translate.formatMessage(commonMessage.update)
                                : translate.formatMessage(messages.uploadImage)}
                        </div>
                    </div>
                </div>

                {visible && (
                    <BaseModal
                        isModalVisible={visible}
                        handleCancel={handleCancel}
                        title={translate.formatMessage(messages.title)}
                        width="800px"
                        noBodyPadding={true}
                        customFooter={
                            <Flex justify="end" gap={8} style={{ padding: '8px 16px' }}>
                                <Button danger onClick={handleCancel} icon={<StopOutlined />}>
                                    {translate.formatMessage(commonMessage.cancel)}
                                </Button>
                                <Button
                                    type="primary"
                                    disabled={!selectedImage}
                                    onClick={handleOk}
                                    icon={<CheckOutlined />}
                                >
                                    {translate.formatMessage(commonMessage.confirm)}
                                </Button>
                            </Flex>
                        }
                    >
                        <Tabs defaultActiveKey="1" style={{ padding: '0 16px' }}>
                            <TabPane tab={translate.formatMessage(messages.imageLibrary)} key="1">
                                <div
                                    ref={scrollContainerRef}
                                    style={{
                                        height: 630,
                                        overflowY: 'auto',
                                        overflowX: 'hidden',
                                    }}
                                >
                                    {useInternalFetch && loadingMore && displayImages.length === 0 && (
                                        <div style={{ textAlign: 'center', padding: '24px 0' }}>
                                            <Spin />
                                        </div>
                                    )}

                                    <List
                                        grid={{ gutter: 16, column: 6 }}
                                        dataSource={displayImages}
                                        renderItem={(item) => (
                                            <List.Item>
                                                <div
                                                    onClick={() => setSelectedImage(item)}
                                                    style={{
                                                        border: selectedImage === item
                                                            ? '3px solid #1890ff' 
                                                            : '1px solid #d9d9d9',
                                                        boxShadow: selectedImage === item 
                                                            ? '0 0 8px rgba(24, 144, 255, 0.6)' 
                                                            : 'none',
                                                        borderRadius: 8,
                                                        cursor: 'pointer',
                                                        padding: 4,
                                                        background: '#171717',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        aspectRatio: '1 / 1',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <Image
                                                        src={getFullImageUrl(item,imagesRootUrl)}
                                                        width="100%"
                                                        preview={false}
                                                    />
                                                </div>
                                            </List.Item>
                                        )}
                                    />

                                    {useInternalFetch && (
                                        <div ref={sentinelRef} style={{ height: 1 }} />
                                    )}

                                    {useInternalFetch && loadingMore && displayImages.length > 0 && (
                                        <div style={{ textAlign: 'center', paddingBottom: '12px' }}>
                                            <Spin size="small" />
                                        </div>
                                    )}
                                </div>
                            </TabPane>

                            <TabPane tab={translate.formatMessage(messages.uploadImage)} key="2">
                                <div
                                    style={{
                                        height: 500,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <CropImageFieldModal
                                        accept={accept}
                                        uploadFile={onUploadFile}
                                        onUploadSuccess={() => {
                                            handleCancel();
                                        }}
                                        aspect={1 / 1}
                                        isNoCrop={isNoCrop}
                                    />
                                </div>
                            </TabPane>
                        </Tabs>
                    </BaseModal>
                )}
            </div>
        </Form.Item>
    );
}

export default ImagePickerModalField;